import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { isNil } from 'lodash';
import { StatusIndicator, HorizontalSplitOutlinedIcon, SlowMotionVideoOutlinedIcon, ViewQuiltOutlinedIcon } from 'tuui';
import { useCurrentValueGetter, useDimension } from '../../../../hooks';
import { PARAMS_TO_KEEP } from '../../../../modules/campaigns/components/CampaignPickerListItem/CampaignPickerListItem';
import { ENTITY, getCampaignStatusType, getStatusIndicatorType } from '../../../../modules/campaigns/config';
import { AllEntityId } from '../../../../modules/campaigns/config/allEntityId';
import { fetchCampaigns } from '../../../../modules/campaigns/flows';
import { useSelectedCampaign } from '../../../../modules/campaigns/hooks/useSelectedCampaign';
import { useSelectedCampaignGroup } from '../../../../modules/campaigns/hooks/useSelectedCampaignGroup';
import { isDisplayCampaign } from '../../../../modules/campaigns/modules/creative-display/utils/isDisplayCampaign';
import { useAccount } from '../../../../modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from '../../../../modules/taboola-common-frontend-modules/i18n';
import { CAMPAIGN_DIMENSION } from '../../../../services/campaignDimension';
import { useBreadcrumbNavigation } from '../../hooks/useBreadcrumbNavigation';
import { useCampaignPickerSorting } from '../../hooks/useCampaignPickerSorting';
import { usePickerTotal } from '../../hooks/usePickerTotal';
import { BreadcrumbsDropdown } from '../BreadcrumbsDropdown/BreadcrumbsDropdown';
import { BreadcrumbsSearchComponent } from '../BreadcrumbsDropdown/components/BreadcrumbsSearchComponent/BreadcrumbsSearchComponent';
import { CampaignSortComponent } from '../CampaignSortComponent/CampaignSortComponent';
import DropdownCampaignEditButton from '../DropdownCampaignEditButton/DropdownCampaignEditButton';
import { DropdownOptionRenderer } from '../DropdownOptionRenderer/DropdownOptionRenderer';
import commonStyles from '../commonPickerDropdownStyle.module.scss';

const CAMPAIGN_PICKER_KEY_PREFIX = 'CAMPAIGN_PICKER_KEY_PREFIX';
const CAMPAIGN_QUERY_KEY_PREFIX = 'campaign-picker';

const getIcon = campaign =>
    campaign?.dimension === CAMPAIGN_DIMENSION.VIDEO ? (
        <SlowMotionVideoOutlinedIcon />
    ) : isDisplayCampaign(campaign) ? (
        <ViewQuiltOutlinedIcon />
    ) : (
        <HorizontalSplitOutlinedIcon />
    );

const OptionRenderer = props => {
    const {
        data: { id, status },
    } = props;

    const statusType = getCampaignStatusType(status);
    const statusIndicatorType = getStatusIndicatorType(statusType);
    const statusContent =
        statusIndicatorType && id !== AllEntityId ? (
            <StatusIndicator type={statusIndicatorType} className={commonStyles['icon-status']} />
        ) : (
            <span />
        );
    const iconContent =
        id === AllEntityId ? (
            <span />
        ) : (
            <>
                {getIcon(props.data) || null}
                {statusContent}
            </>
        );

    return (
        <DropdownOptionRenderer
            {...props}
            idPropName="id"
            namePropName="name"
            icon={iconContent}
            entity={ENTITY.CAMPAIGN}
            editButtonComponent={DropdownCampaignEditButton}
        />
    );
};

export const BreadcrumbsCampaignPicker = ({ mainContainerWidth }) => {
    const dispatch = useDispatch();
    const account = useAccount();
    const { accountName } = account;
    const { campaign: selectedCampaign } = useSelectedCampaign();
    const [dimension] = useDimension();
    const { campaignGroup: selectedCampaignGroup } = useSelectedCampaignGroup();
    const campaignGroupGetter = useCurrentValueGetter(selectedCampaignGroup);
    const { sortGetter } = useCampaignPickerSorting();

    const fetcher = useCallback(
        async ({ searchText, pageSize, hasMore, page, queryKey }) => {
            const campaignGroupId = campaignGroupGetter()?.campaignsGroupId;

            const [results, total] = await dispatch(
                fetchCampaigns({
                    accountName,
                    page,
                    pageSize,
                    searchText,
                    sort: sortGetter(),
                    apiCacheKey: queryKey,
                    includeCreativeLibraryCampaigns: true,
                    ...(campaignGroupId === AllEntityId || isNil(campaignGroupId) ? {} : { campaignGroupId }),
                })
            );
            if (isNil(total)) {
                return { items: [], hasMore: false, total: 0 };
            }
            return { items: results, hasMore, total };
        },
        [accountName, dispatch, campaignGroupGetter, sortGetter]
    );

    const totalCount = usePickerTotal({
        fetcher,
        enabled: selectedCampaign.id === AllEntityId && !isNil(account.accountName),
        queryKey: [
            CAMPAIGN_PICKER_KEY_PREFIX,
            account?.accountName,
            selectedCampaignGroup?.campaignsGroupId,
            dimension,
        ],
    });

    const allOptionsValue = isNil(selectedCampaign?.id)
        ? null
        : {
              id: AllEntityId,
              name: <FormattedMessage id="app.breadcrumbs.campaign.all" values={{ count: '' }} />,
              hideId: true,
          };

    const handleSelectedOption = useBreadcrumbNavigation({
        entityType: ENTITY.CAMPAIGN,
        paramsToKeep: PARAMS_TO_KEEP,
        selectedItem: selectedCampaign,
    });

    const campaignValue =
        isNil(selectedCampaign?.id) || selectedCampaign.id === AllEntityId ? (
            <FormattedMessage
                id="app.breadcrumbs.campaign.all"
                defaultMessage="All Campaigns"
                values={{ count: isNil(totalCount) ? '' : `(${totalCount})` }}
            />
        ) : (
            selectedCampaign.name
        );

    const alignmentTrigger =
        account?.accountName +
        selectedCampaignGroup?.campaignsGroupId +
        selectedCampaign.id +
        dimension +
        mainContainerWidth;
    return (
        <BreadcrumbsDropdown
            label={
                <FormattedMessage
                    id="app.breadcrumbs.campaign"
                    defaultMessage="Campaign"
                    className={commonStyles['label']}
                />
            }
            value={campaignValue}
            allOption={allOptionsValue}
            fetcher={fetcher}
            onSelect={handleSelectedOption}
            ItemComponent={OptionRenderer}
            SearchComponent={BreadcrumbsSearchComponent}
            SortComponent={CampaignSortComponent}
            selectedItem={selectedCampaign}
            alignmentTrigger={alignmentTrigger}
            queryKey={[
                CAMPAIGN_QUERY_KEY_PREFIX,
                account?.accountName,
                selectedCampaignGroup?.campaignsGroupId,
                dimension,
                JSON.stringify(sortGetter()),
            ]}
            dataAutomationId="breadcrumbs-campaign-picker"
        />
    );
};
